import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  Card,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@rumbledirect/ui-v2";
import type { VehicleType } from "@/configurator/vehicleTypes/vehicleType";
import { toVehicleTypePatchInput } from "@/configurator/vehicleTypes/vehicleTypeForm";
import { VehicleTypeDetailsTab } from "@/configurator/vehicleTypes/VehicleTypeDetailsTab";
import { VehicleTypeMediaTab } from "@/configurator/vehicleTypes/VehicleTypeMediaTab";
import { VehicleTypeOptionsTab } from "@/configurator/vehicleTypes/VehicleTypeOptionsTab";
import { VehicleTypeServicePartnersTab } from "@/configurator/vehicleTypes/VehicleTypeServicePartnersTab";
import { useUpdateVehicleType } from "@/configurator/vehicleTypes/useUpdateVehicleType";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { useGetAdminConfiguratorVehicleTypesId } from "@/lib/api/generated/configurator/configurator";

const UPDATE_PERMISSION = "admin_configurator_vehicle_type_update";
/** Ties the header save button to the details form rendered inside the tabs. */
const DETAILS_FORM_ID = "vehicle-type-details-form";

const DETAIL_TABS = ["details", "opties", "servicepartners", "media"] as const;
type DetailTab = (typeof DETAIL_TABS)[number];
const DEFAULT_TAB: DetailTab = "details";

function isDetailTab(value: unknown): value is DetailTab {
  return DETAIL_TABS.includes(value as DetailTab);
}

interface VehicleTypeEditSearch {
  readonly tab: DetailTab;
}

export const Route = createFileRoute(
  "/_authenticated/configurator/vehicle-types_/$vehicleTypeId_/edit",
)({
  validateSearch: (search: Record<string, unknown>): VehicleTypeEditSearch => ({
    tab: isDetailTab(search.tab) ? search.tab : DEFAULT_TAB,
  }),
  component: VehicleTypeEditPage,
});

function VehicleTypeEditPage(): ReactNode {
  const { vehicleTypeId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const user = useCurrentUser();
  const canUpdate = hasPermission(user, UPDATE_PERMISSION);

  const { data, isPending, isError, refetch } =
    useGetAdminConfiguratorVehicleTypesId(vehicleTypeId);
  const updateMutation = useUpdateVehicleType(vehicleTypeId);

  const title = data
    ? `${data.brand} ${data.model}`.trim() || "Voertuigtype"
    : "Voertuigtype";

  return (
    <div className="space-y-5">
      <Link
        to="/configurator/vehicle-types/$vehicleTypeId"
        params={{ vehicleTypeId }}
        search={{ tab: "details" }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Terug naar voertuigtype
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4">
        {isPending ? (
          <Skeleton className="h-8 w-64" />
        ) : (
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        )}

        {data && canUpdate && (
          <Button
            type="submit"
            form={DETAILS_FORM_ID}
            size="sm"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="size-4" aria-hidden="true" />
            )}
            Wijzigingen opslaan
          </Button>
        )}
      </div>

      {isError ? (
        <Card className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="text-muted-foreground">
            We konden dit voertuigtype niet laden.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Opnieuw proberen
          </Button>
        </Card>
      ) : isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <VehicleTypeEditBody
          vehicleType={data}
          tab={tab}
          canUpdate={canUpdate}
          onTabChange={(nextTab) =>
            void navigate({ search: (prev) => ({ ...prev, tab: nextTab }) })
          }
          onSaveDetails={(values) =>
            updateMutation.mutateAsync(toVehicleTypePatchInput(values))
          }
        />
      )}
    </div>
  );
}

interface VehicleTypeEditBodyProps {
  readonly vehicleType: VehicleType;
  readonly tab: DetailTab;
  readonly canUpdate: boolean;
  readonly onTabChange: (tab: DetailTab) => void;
  readonly onSaveDetails: (
    values: Parameters<typeof toVehicleTypePatchInput>[0],
  ) => Promise<unknown>;
}

function VehicleTypeEditBody({
  vehicleType,
  tab,
  canUpdate,
  onTabChange,
  onSaveDetails,
}: VehicleTypeEditBodyProps): ReactNode {
  const id = vehicleType.id;

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => {
        if (isDetailTab(value)) onTabChange(value);
      }}
      className="flex flex-col gap-5"
    >
      <TabsList className="self-start">
        <TabsTrigger value="details">Details</TabsTrigger>
        <TabsTrigger value="opties">Opties</TabsTrigger>
        <TabsTrigger value="servicepartners">Servicepartners</TabsTrigger>
        <TabsTrigger value="media">Media</TabsTrigger>
      </TabsList>

      {/* Kept mounted (not a TabsContent) so its form state survives tab
          switches and the header save button can submit it from any tab. */}
      <div hidden={tab !== "details"} role="tabpanel">
        <Card className="p-6">
          <VehicleTypeDetailsTab
            formId={DETAILS_FORM_ID}
            vehicleType={vehicleType}
            canUpdate={canUpdate}
            onSave={onSaveDetails}
          />
        </Card>
      </div>

      <TabsContent value="opties">
        <Card className="p-6">
          <VehicleTypeOptionsTab
            vehicleTypeId={id}
            options={vehicleType.options}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>

      <TabsContent value="servicepartners">
        <Card className="p-6">
          <VehicleTypeServicePartnersTab
            vehicleTypeId={id}
            servicePartners={vehicleType.servicePartners}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>

      <TabsContent value="media">
        <Card className="p-6">
          <VehicleTypeMediaTab
            vehicleTypeId={id}
            media={vehicleType.media}
            canUpdate={canUpdate}
          />
        </Card>
      </TabsContent>
    </Tabs>
  );
}
